import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';

import type { Placement } from '@floating-ui/core';
import {
    autoPlacement,
    autoUpdate,
    flip as floatingFlip,
    offset as floatingOffset,
    useFloating,
} from '@floating-ui/react-dom';

import { styled } from '../theme';

const popoverRoot = document.getElementById('popover-root')!;

const StyledSection = styled('section')({
    position: 'relative',
});

const PopoverContainer = styled('div')(({ theme }) => ({
    zIndex: theme.zIndex.popovers,
    opacity: 0,
    animation: theme.animations.fadeIn,
    position: 'absolute',
}));

const PopoverView = styled('div', {
    shouldForwardProp: (prop) => prop !== 'placementMargin',
})<{ placementMargin: number }>(({ theme, placementMargin }) => ({
    minWidth: 200,
    maxHeight: '50vh',
    overflowY: 'overlay' as const,
    backgroundColor: theme.color.white,
    boxShadow: theme.boxShadow.surface,
    borderRadius: theme.borderRadius.base,

    '&[data-placement*="bottom"]': {
        marginTop: placementMargin,
    },
    '&[data-placement*="top"]': {
        marginBottom: placementMargin,
    },
    '&[data-placement*="right"]': {
        marginLeft: placementMargin,
    },
    '&[data-placement*="left"]': {
        marginRight: placementMargin,
    },
}));

type RenderProps = { toggleVisibility: () => void; visibility: boolean };

interface Props {
    style?: React.CSSProperties;
    className?: string;
    containerClassName?: string;
    contentStyle?: React.CSSProperties;
    children: (renderProps: RenderProps & { ref: React.Ref<HTMLElement> }) => React.ReactNode;
    renderContent: (renderProps: RenderProps) => React.ReactNode;
    placement?: Placement;
    offset?: number;
    placementMargin?: number;
    disableAutoPlacement?: boolean;
    flip?: boolean;
    disableHideOnInnerClick?: boolean;
    disableHideOnOuterClick?: boolean;
    onVisibilityChange?: (value: boolean) => void;
}

const Popover = forwardRef<{ setPopoverVisibility: (value: boolean) => void }, Props>(
    (
        {
            style,
            className,
            containerClassName,
            contentStyle,
            children,
            renderContent,
            placement = 'auto-start',
            offset = 0,
            placementMargin = 16,
            disableAutoPlacement = false,
            flip = false,
            disableHideOnInnerClick = false,
            disableHideOnOuterClick = false,
            onVisibilityChange,
        },
        ref
    ) => {
        const [visibility, setVisibility] = useState(false);

        const setPopoverVisibility = useCallback(
            (value: boolean) => {
                setVisibility(value);
                onVisibilityChange?.(value);
            },
            [onVisibilityChange]
        );

        const toggleVisibility = useCallback(() => {
            setVisibility((v) => {
                const nextVisibility = !v;
                onVisibilityChange?.(nextVisibility);
                return nextVisibility;
            });
        }, [onVisibilityChange]);

        useImperativeHandle(ref, () => ({ setPopoverVisibility }), [setPopoverVisibility]);

        const {
            x,
            y,
            strategy,
            refs,
            placement: finalPlacement,
        } = useFloating({
            placement: placement as Placement,
            middleware: [
                floatingOffset(offset),
                ...(disableAutoPlacement ? [] : [autoPlacement({ alignment: 'start' })]),
                ...(flip ? [floatingFlip()] : []),
            ],
            whileElementsMounted: autoUpdate,
        });

        const handleClickOutside = useCallback(
            (event: MouseEvent) => {
                if (!visibility || disableHideOnOuterClick) return;
                const refEl = refs.reference.current as HTMLElement | null;
                const floatEl = refs.floating.current;
                if (
                    refEl &&
                    !refEl.contains(event.target as Node) &&
                    floatEl &&
                    !floatEl.contains(event.target as Node)
                ) {
                    setPopoverVisibility(false);
                }
            },
            [visibility, disableHideOnOuterClick, refs, setPopoverVisibility]
        );

        const handleInnerClick = useCallback(() => {
            if (!disableHideOnInnerClick) {
                setPopoverVisibility(false);
            }
        }, [disableHideOnInnerClick, setPopoverVisibility]);

        useEffect(() => {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [handleClickOutside]);

        return (
            <StyledSection className={containerClassName}>
                {children({ ref: refs.setReference, toggleVisibility, visibility })}

                {visibility &&
                    createPortal(
                        <PopoverContainer
                            ref={refs.setFloating}
                            className={className}
                            data-placement={finalPlacement}
                            style={{
                                top: y ?? 0,
                                left: x ?? 0,
                                position: strategy,
                                ...style,
                            }}
                        >
                            <PopoverView
                                data-placement={finalPlacement}
                                placementMargin={placementMargin}
                                style={contentStyle}
                                onClick={handleInnerClick}
                            >
                                {renderContent({ toggleVisibility, visibility })}
                            </PopoverView>
                        </PopoverContainer>,
                        popoverRoot
                    )}
            </StyledSection>
        );
    }
);

Popover.displayName = 'Popover';

export default Popover;
